import { useState, useContext, useRef } from 'react';
import axios from 'axios';
import { ReportContext, PageContext } from '../Context';
import { TextField, Button, Box, Paper, Tooltip, Zoom } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { styled } from '@mui/material/styles';

// Style personnalisé pour le formulaire de recherche
const SearchPaper = styled(Paper)(({ theme, hasError, isShaking }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0.5, 2),
  borderRadius: 22,
  boxShadow: hasError 
    ? '0 0 0 2px rgba(211, 47, 47, 0.5)' 
    : '0 2px 5px rgba(0, 0, 0, 0.08)',
  '&:hover': {
    boxShadow: hasError 
      ? '0 0 0 2px rgba(211, 47, 47, 0.7)' 
      : '0 4px 8px rgba(0, 0, 0, 0.12)',
  },
  transition: 'box-shadow 0.3s ease, transform 0.1s ease',
  marginRight: theme.spacing(2),
  animation: isShaking ? 'shake 0.5s ease-in-out' : 'none',
  '@keyframes shake': {
    '0%, 100%': { transform: 'translateX(0)' },
    '10%, 30%, 50%, 70%, 90%': { transform: 'translateX(-4px)' },
    '20%, 40%, 60%, 80%': { transform: 'translateX(4px)' },
  },
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    borderRadius: 8,
    padding: theme.spacing(1.5),
  }
}));

function ReportSearch() {
  const { pickReport } = useContext(ReportContext);
  const { setPage } = useContext(PageContext);
  const [number, setNumber] = useState('');
  const [year, setYear] = useState('');
  const [error, setError] = useState('');
  const [shake, setShake] = useState(false);
  const numberInputRef = useRef(null);
  const yearInputRef = useRef(null);

  // Fonction pour déclencher l'animation de shake
  const triggerShake = () => {
    setShake(true);
    setTimeout(() => setShake(false), 500);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!number || !year) {
      setError('Numéro et année requis');
      triggerShake();
      return;
    }
    
    // Validation du format numérique : exactement 6 chiffres pour le numéro ET 4 pour l'année
    if (!/^\d{6}$/.test(number) || !/^\d{4}$/.test(year)) {
      setError('Format : 6 chiffres / 4 chiffres');
      triggerShake();
      return;
    }

    try {
      const token = sessionStorage.getItem('authToken');
      const response = await axios.get(`/api/report/search`, {
        params: { number, year },
        headers: { Authorization: `Bearer ${token}` },
      });

      // PV existe, redirection vers Edition avec toutes les données
      pickReport({ number: response.data.number, year: response.data.year, new: false });
      setPage('Edition');

    } catch (err) {
      if (err.response?.status === 404) {
        // PV n'existe pas, redirection vers Edition avec pré-remplissage
        pickReport({ number, year, new: true });
        setPage('Edition');
      } else {
        setError('Erreur de recherche');
        triggerShake();
      }
    }
  };

  // Formatage automatique du numéro pendant la saisie
  const handleNumberChange = (e) => {
    const value = e.target.value.replace(/\D/g, ''); // Supprime tous les caractères non numériques
    if (value.length <= 6) {
      setNumber(value);
      
      // Si l'utilisateur entre 6 chiffres, passer au champ année
      if (value.length === 6 && yearInputRef.current) {
        yearInputRef.current.focus();
      }
    }
  };

  // Formatage automatique de l'année pendant la saisie
  const handleYearChange = (e) => {
    const value = e.target.value.replace(/\D/g, ''); // Supprime tous les caractères non numériques
    if (value.length <= 4) {
      setYear(value);
    }
  };

  // Effacer l'erreur quand l'utilisateur commence à taper
  const handleNumberChangeWithClear = (e) => {
    if (error) setError('');
    handleNumberChange(e);
  };

  const handleYearChangeWithClear = (e) => {
    if (error) setError('');
    handleYearChange(e);
  };

  return (
    <Box component="div" sx={{ my: 0.75 }}>
      <form onSubmit={handleSubmit} autoComplete="off">
        <Tooltip 
          title={error} 
          open={!!error} 
          arrow 
          placement="bottom"
          TransitionComponent={Zoom}
          slotProps={{
            tooltip: {
              sx: {
                bgcolor: 'error.main',
                color: 'white',
                fontSize: '0.8rem',
                fontWeight: 500,
                '& .MuiTooltip-arrow': {
                  color: 'error.main',
                },
              },
            },
          }}
        >
          <SearchPaper elevation={0} hasError={!!error} isShaking={shake}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <TextField
                id="num_report_search"
                name="num_report_search"
                variant="standard"
                placeholder="Numéro"
                value={number}
                onChange={handleNumberChangeWithClear}
                inputRef={numberInputRef}
                InputProps={{
                  disableUnderline: true,
                }}
                sx={{ 
                  mx: 1, 
                  '& input': { 
                    textAlign: 'center',
                    fontWeight: 'medium',
                    fontSize: '1.15rem',
                  }
                }}
              />
              
              <Box component="span" sx={{ mx: 0.5, color: 'text.secondary', fontSize: '1.15rem' }}>/</Box>
              
              <TextField
                id="year_report_search"
                name="year_report_search"
                variant="standard"
                placeholder="Année"
                value={year}
                onChange={handleYearChangeWithClear}
                inputRef={yearInputRef}
                InputProps={{
                  disableUnderline: true,
                }}
                sx={{ 
                  mx: 1, 
                  width: '60px',
                  '& input': { 
                    textAlign: 'center',
                    fontWeight: 'medium',
                    fontSize: '1.15rem',
                  }
                }}
              />
              
              <Button
                type="submit"
                color="primary"
                sx={{ 
                  minWidth: 'auto',
                  borderRadius: '50%',
                  p: 0.85,
                  ml: 1
                }}
              >
                <SearchIcon sx={{ fontSize: '1.3rem' }} />
              </Button>
            </Box>
          </SearchPaper>
        </Tooltip>
      </form>
    </Box>
  );
}

export default ReportSearch;